import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { useTranslation } from 'react-i18next'
import { tw } from 'utils/classnames'
import { useAidaPanelImageDrop } from '../../hooks/use-aida-panel-image-drop'
import { useAidaFileDropStore } from '../../stores/file-drop-store'
import { useAidaPipStore } from '../../stores/picture-in-picture-store'
import { AidaImageDropOverlay } from '../shell/aida-image-drop-chrome'
import { AidaPipAwayNotice } from '../shell/aida-picture-in-picture'

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

export function AidaFullscreenPanel({
  showShell,
  setFullscreenSlot,
  portalContainerRef,
  ref,
  ...props
}: {
  showShell: boolean
  setFullscreenSlot: (node: HTMLDivElement | null) => void
  portalContainerRef: React.RefObject<HTMLDivElement | null>
  ref?: React.Ref<HTMLDivElement>
} & Omit<React.ComponentProps<'div'>, 'ref'>) {
  const { t } = useTranslation('components')
  const pipActive = useAidaPipStore(state => state.active)
  const drop = useAidaPanelImageDrop()
  const fileOver = useAidaFileDropStore(state => state.over)

  return (
    <div
      data-aida-shell="fullscreen"
      className={tw(
        'fixed inset-3 overflow-hidden rounded-4 bg-theme_semantic_surface_primary-background-elevated shadow-3-smooth outline-none ring-1 ring-inset ring-theme_semantic_surface_border_color-subtle',
        showShell && 'invisible',
      )}
      {...props}
      ref={node => {
        assignRef(ref, node)
        portalContainerRef.current = node
        drop.setDropTarget(node)
      }}
    >
      <Dialog.Title className="sr-only">{t('studio.components.aida.title')}</Dialog.Title>
      <div
        ref={showShell ? undefined : setFullscreenSlot}
        className="isolate size-full min-h-0 overflow-hidden"
      >
        {pipActive ? <AidaPipAwayNotice /> : null}
      </div>
      {drop.showDrop || fileOver ? (
        <AidaImageDropOverlay over={drop.isOver || fileOver} kind={fileOver ? 'file' : 'image'} />
      ) : null}
    </div>
  )
}
